// File: src/components/admin/SubscriptionOverviewView.js
// admin Subscription overview dashboard.
// Mounts at /subscription. Reads everything from a single
// GET /admin/subscription/overview call (cached BE-side for 60s).

import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Card, CardContent, Stack, Typography, Grid, Button, Alert,
    CircularProgress, Divider, Tooltip, Table, TableBody, TableCell,
    TableHead, TableRow,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Warning as WarningIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { mainApi } from '../../services/mainApi';

const STATE_LABELS = {
    TRIAL: 'Trial',
    FREE: 'Free',
    PAID: 'Paid',
    PAID_CANCELLED: 'Paid · cancelling',
};

function formatCurrency(cents, currency = 'EUR') {
    if (cents == null) return '—';
    const amount = cents / 100;
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency', currency, maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `${amount.toFixed(2)} ${currency}`;
    }
}

function formatTime(ms) {
    if (!ms) return '—';
    const d = new Date(ms);
    return d.toLocaleString();
}

function HeroCard({ title, value, sub, testid }) {
    return (
        <Card variant="outlined" data-testid={testid} sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="overline" color="text.secondary">{title}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, mt: 0.5 }}>{value}</Typography>
                {sub && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{sub}</Typography>}
            </CardContent>
        </Card>
    );
}

function BarRow({ label, count, total, testid, color = 'primary.main' }) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <Box data-testid={testid} sx={{ mb: 1.2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{label}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {count}{total > 0 && ` · ${pct}%`}
                </Typography>
            </Box>
            <Box sx={{ height: 8, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, transition: 'width 200ms' }} />
            </Box>
        </Box>
    );
}

export default function SubscriptionOverviewView() {
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async (fresh = false) => {
        try {
            fresh ? setRefreshing(true) : setLoading(true);
            setError(null);
            const data = await mainApi.getSubscriptionOverview({ fresh });
            setOverview(data);
        } catch (e) {
            setError(e?.response?.data?.error || e.message || 'Failed to load overview');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { load(false); }, [load]);

    if (loading) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }} data-testid="sub-overview-loading">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" data-testid="sub-overview-error">{error}</Alert>
            </Box>
        );
    }

    if (!overview) return null;

    const { counts, trial, revenue, credits, usage, plans, recentTransitions } = overview;
    const proPlan = plans?.find(p => p.id === 'pro');
    const freePlan = plans?.find(p => p.id === 'free');
    const currency = revenue?.currency || 'EUR';
    const isEmpty = counts.total === 0;

    return (
        <Box sx={{ p: 3 }} data-testid="sub-overview-view">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>Subscription Overview</Typography>
                    <Typography variant="body2" color="text.secondary">
                        As of {formatTime(overview.asOf)}
                    </Typography>
                </Box>
                <Button
                    data-testid="sub-overview-refresh"
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={() => load(true)}
                    disabled={refreshing}
                >
                    Refresh
                </Button>
            </Box>

            {isEmpty && (
                <Alert severity="info" sx={{ mb: 2 }} data-testid="sub-overview-empty">
                    No users yet. The dashboard will populate as accounts are created.
                </Alert>
            )}

            {/* Hero cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <HeroCard
                        testid="sub-overview-total-users"
                        title="Users"
                        value={counts.total}
                        sub={`${counts.byPlan.pro} Pro · ${counts.byPlan.free} Free`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <HeroCard
                        testid="sub-overview-mrr"
                        title="MRR"
                        value={formatCurrency(revenue.mrrCents, currency)}
                        sub={revenue.atRiskNext30dCents > 0
                            ? `${formatCurrency(revenue.atRiskNext30dCents, currency)} at risk (cancelling)`
                            : 'No churn risk'}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <HeroCard
                        testid="sub-overview-trials-active"
                        title="Active trials"
                        value={trial.active}
                        sub={`${trial.endingWithin7d} ending in 7d · ${trial.endingWithin30d} in 30d`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <HeroCard
                        testid="sub-overview-paid"
                        title="Paid subscribers"
                        value={counts.byState.PAID}
                        sub={counts.byState.PAID_CANCELLED > 0
                            ? `${counts.byState.PAID_CANCELLED} cancelling at period end`
                            : 'No cancellations'}
                    />
                </Grid>
            </Grid>

            {/* Mix cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" data-testid="sub-overview-plan-mix">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>Plan mix</Typography>
                            <BarRow
                                testid="sub-overview-plan-mix-row-free"
                                label="Free" count={counts.byPlan.free} total={counts.total}
                                color="grey.500"
                            />
                            <BarRow
                                testid="sub-overview-plan-mix-row-pro"
                                label="Pro" count={counts.byPlan.pro} total={counts.total}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" data-testid="sub-overview-state-mix">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>State mix</Typography>
                            {(['TRIAL', 'FREE', 'PAID', 'PAID_CANCELLED']).map((s) => (
                                <BarRow
                                    key={s}
                                    testid={`sub-overview-state-row-${s}`}
                                    label={STATE_LABELS[s]}
                                    count={counts.byState[s] || 0}
                                    total={counts.total}
                                    color={s === 'PAID_CANCELLED' ? 'warning.main' : 'primary.main'}
                                />
                            ))}
                            {trial.expiredButNotTransitioned > 0 && (
                                <Alert severity="warning" sx={{ mt: 1.5 }} icon={<WarningIcon />}>
                                    {trial.expiredButNotTransitioned} expired trial
                                    {trial.expiredButNotTransitioned === 1 ? '' : 's'} not yet transitioned —
                                    expiry cron may be lagging.
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Top consumers */}
            <Card variant="outlined" sx={{ mb: 2 }} data-testid="sub-overview-top-consumers">
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
                            Top AI-action consumers this month
                        </Typography>
                        {usage.overQuotaCount > 0 && (
                            <Button
                                size="small"
                                component={RouterLink}
                                to="/subscription/users?overQuota=1"
                                data-testid="sub-overview-view-overquota"
                            >
                                {usage.overQuotaCount} over quota →
                            </Button>
                        )}
                    </Box>
                    {usage.topConsumersThisMonth.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No AI activity yet this month.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Plan</TableCell>
                                    <TableCell align="right">Used</TableCell>
                                    <TableCell align="right">Cap</TableCell>
                                    <TableCell align="right">%</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {usage.topConsumersThisMonth.map((u, i) => (
                                    <TableRow
                                        key={u.uid}
                                        hover
                                        data-testid={`sub-overview-top-consumers-row-${i}`}
                                        component={RouterLink}
                                        to={`/users/${u.uid}/subscription`}
                                        sx={{ textDecoration: 'none', cursor: 'pointer' }}
                                    >
                                        <TableCell>{u.email || u.uid}</TableCell>
                                        <TableCell>{u.planId}</TableCell>
                                        <TableCell align="right">{u.aiActionsThisMonth.toLocaleString()}</TableCell>
                                        <TableCell align="right">{u.cap === 0 ? '∞' : u.cap.toLocaleString()}</TableCell>
                                        <TableCell align="right">
                                            {u.pctOfCap == null ? '—' : (
                                                <Tooltip title={u.pctOfCap >= 100 ? 'Over quota' : ''}>
                                                    <span style={{ color: u.pctOfCap >= 100 ? '#d32f2f' : undefined }}>
                                                        {u.pctOfCap}%
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Recent transitions */}
            <Card variant="outlined" sx={{ mb: 2 }} data-testid="sub-overview-recent">
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Recent state changes
                    </Typography>
                    {recentTransitions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No recent transitions.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>When</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Change</TableCell>
                                    <TableCell>Actor</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recentTransitions.map((t, i) => (
                                    <TableRow
                                        key={`${t.uid}-${t.at}-${i}`}
                                        hover
                                        data-testid={`sub-overview-recent-row-${i}`}
                                        component={RouterLink}
                                        to={`/users/${t.uid}/subscription`}
                                        sx={{ textDecoration: 'none', cursor: 'pointer' }}
                                    >
                                        <TableCell>{formatTime(t.at)}</TableCell>
                                        <TableCell>{t.email || t.uid}</TableCell>
                                        <TableCell>
                                            {(t.from || '—')} → <strong>{t.to || '—'}</strong>
                                        </TableCell>
                                        <TableCell>{t.actor}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Credits summary */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" data-testid="sub-overview-credits">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Manual credit grants</Typography>
                            {credits.usersWithManualGrants === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No manual grants outstanding.
                                </Typography>
                            ) : (
                                <Typography variant="body2">
                                    <strong>{credits.usersWithManualGrants}</strong> user
                                    {credits.usersWithManualGrants === 1 ? '' : 's'} hold a total of{' '}
                                    <strong>{credits.totalAiActionsGranted.toLocaleString()}</strong> AI actions.
                                    {credits.expiringWithin30d > 0 && (
                                        <> · {credits.expiringWithin30d} grant
                                            {credits.expiringWithin30d === 1 ? '' : 's'} expire within 30 days.</>
                                    )}
                                </Typography>
                            )}
                            <Button
                                size="small"
                                component={RouterLink}
                                to="/subscription/credits"
                                sx={{ mt: 1 }}
                                data-testid="sub-overview-view-credits"
                            >
                                View grants
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" data-testid="sub-overview-churn">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Churn risk</Typography>
                            <Typography variant="body2">
                                <strong>{counts.byState.PAID_CANCELLED}</strong> subscriber
                                {counts.byState.PAID_CANCELLED === 1 ? '' : 's'} cancelling at period end · {' '}
                                {formatCurrency(revenue.atRiskNext30dCents, currency)} at risk in next 30 days.
                            </Typography>
                            <Button
                                size="small"
                                component={RouterLink}
                                to="/subscription/users?state=PAID_CANCELLED"
                                sx={{ mt: 1 }}
                                data-testid="sub-overview-view-churn"
                            >
                                View cancelling users
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Plan configuration summary */}
            <Card variant="outlined" data-testid="sub-overview-plan-config">
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>Plan configuration</Typography>
                        <Button
                            size="small"
                            startIcon={<EditIcon />}
                            component={RouterLink}
                            to="/plans"
                            data-testid="sub-overview-edit-plans"
                        >
                            Edit plans
                        </Button>
                    </Box>
                    <Grid container spacing={2}>
                        {[freePlan, proPlan].filter(Boolean).map(p => (
                            <Grid item xs={12} md={6} key={p.id}>
                                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}
                                    data-testid={`sub-overview-plan-summary-${p.id}`}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {p.name} — {formatCurrency(p.price, p.currency)}/{p.interval}
                                    </Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Stack spacing={0.5}>
                                        <Typography variant="body2">
                                            Monthly AI actions: <strong>{p.monthlyAiActions === 0 ? '∞' : p.monthlyAiActions.toLocaleString()}</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            Active campaigns: <strong>{p.activeCampaigns === 0 ? '∞' : p.activeCampaigns}</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            Storage: <strong>{p.storageMb.toLocaleString()} MB</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            API access: <strong>{p.apiEnabled ? 'enabled' : 'disabled'}</strong>
                                            {p.apiEnabled && p.apiMonthlyRequests > 0 && (
                                                <> · {p.apiMonthlyRequests.toLocaleString()}/mo</>
                                            )}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
}
